import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Settings, User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { NotificationBell } from "@/components/NotificationBell";

interface UserMenuProps {
  onOpenTeamMessages?: (recipientId?: string) => void;
  onOpenAccessRequests?: () => void;
}

export const UserMenu = ({ onOpenTeamMessages, onOpenAccessRequests }: UserMenuProps = {}) => {
  const { user, signOut } = useAuth();
  const { data: profile } = useProfile();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);

  useEffect(() => {
    async function getWorkspaceId() {
      if (!user) return;
      
      const { data } = await supabase
        .from('workspace_members')
        .select('workspace_id')
        .eq('user_id', user.id)
        .eq('seat_status', 'active')
        .single();

      if (data) {
        setWorkspaceId(data.workspace_id);
      }
    }

    getWorkspaceId();
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Erfolgreich abgemeldet",
        description: "Sie wurden abgemeldet.",
      });
      navigate("/auth");
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Abmeldung fehlgeschlagen.",
        variant: "destructive",
      });
    }
  };

  if (!user) return null;

  const userInitials = profile?.first_name && profile?.last_name
    ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
    : user.email?.split("@")[0].slice(0, 2).toUpperCase() || "U";
  
  const userName = profile?.name || profile?.first_name || user.email?.split("@")[0] || "User";

  return (
    <div className="flex items-center gap-2">
      <NotificationBell
        workspaceId={workspaceId}
        onOpenTeamMessages={(recipientId) => onOpenTeamMessages?.(recipientId)}
        onOpenAccessRequests={() => onOpenAccessRequests?.()}
      />
      
      <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-full transition-all">
          <Avatar className="h-8 w-8 cursor-pointer hover:opacity-80 transition-opacity">
            {profile?.avatar_url && (
              <AvatarImage src={profile.avatar_url} alt={userName} />
            )}
            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
              {userInitials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-card border-border">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-3 py-2">
            <Avatar className="h-10 w-10">
              {profile?.avatar_url && (
                <AvatarImage src={profile.avatar_url} alt={userName} />
              )}
              <AvatarFallback className="bg-primary text-primary-foreground">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{userName}</p>
              <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setTimeout(() => navigate("/settings/profile"), 0);
          }}
          className="cursor-pointer"
        >
          <User className="mr-2 h-4 w-4" />
          <span>Profil</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setTimeout(() => navigate("/settings/workspace"), 0);
          }}
          className="cursor-pointer"
        >
          <Settings className="mr-2 h-4 w-4" />
          <span>Einstellungen</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Abmelden</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    </div>
  );
};
